import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Typography,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@material-ui/core";
import { Description, GetApp as DownloadIcon } from "@material-ui/icons";
import FileDownload from "./FileDownload";
import TemplateViewer from "./TemplateViewer";
import { useTranslation } from "../hooks/useTranslation";
import ExtractionModeChip from "./ExtractionModeChip";
import { getJobExtractionMode } from "../constants/extractionModes";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
  },
  templateLink: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    cursor: "pointer",
    color: "var(--ikaro-orange-primary)",
    fontSize: "0.75rem",
    fontWeight: 500,
    "&:hover": {
      textDecoration: "underline",
      color: "var(--ikaro-orange-hover)",
    },
  },
  templateIcon: {
    fontSize: "1rem",
    color: "var(--ikaro-orange-primary)",
  },
  dialogPaper: {
    maxWidth: "900px",
    width: "90vw",
    maxHeight: "80vh",
  },
  dialogTitle: {
    padding: theme.spacing(1.5, 2),
    background: "var(--ikaro-gradient-banner)",
    borderBottom: "1px solid var(--ikaro-orange-hover)",
    "& .MuiTypography-h6": {
      fontSize: "1rem",
      fontWeight: 600,
      color: "var(--ikaro-text-white)",
    },
  },
  dialogContent: {
    padding: theme.spacing(2),
    minHeight: "400px",
  },
  dialogActions: {
    padding: theme.spacing(1.5, 2),
    backgroundColor: "var(--ikaro-bg-surface-secondary)",
    borderTop: "1px solid var(--ikaro-border-default)",
    "& .MuiButton-root": {
      color: "var(--ikaro-orange-primary)",
      backgroundColor: "transparent",
      fontWeight: 500,
      borderRadius: "6px",
      textTransform: "none",
      border: "1px solid var(--ikaro-gray-medium)",
      "&:hover": {
        backgroundColor: "var(--ikaro-orange-light)",
        borderColor: "var(--ikaro-orange-primary)",
        transform: "translateY(-1px)",
        boxShadow: "var(--ikaro-shadow-light)",
      },
    },
  },
  fileDownloadContainer: {
    marginTop: theme.spacing(0.25),
  },
  noData: {
    fontSize: "0.75rem",
    color: "var(--ikaro-gray-dark)",
  },
  maxPagesText: {
    fontSize: "0.75rem",
    color: "var(--ikaro-gray-dark)",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  maxPagesLabel: {
    fontWeight: 500,
  },
}));

/**
 * ProcessamentoInputCell - Componente para exibir os inputs de um processamento
 *
 * Exibe:
 * - Chip do modelo de extração (Standard / Next-Set26)
 * - Link "Template" que abre um modal com a visualização do template JSON
 * - FileDownload para o documento de entrada (usando input_params.document)
 *
 * @param {Object} props
 * @param {Object} props.job - O objeto do job com input_payload e input_params
 * @param {boolean} [props.showExtractionMode=true] - Exibe o chip do modelo.
 *   Desligado no drawer de /processamentos, que já mostra o modelo em
 *   "Configuração da Execução".
 */
const ProcessamentoInputCell = ({ job, showExtractionMode = true }) => {
  const classes = useStyles();
  const { t } = useTranslation("jobs");
  const [templateModalOpen, setTemplateModalOpen] = useState(false);

  const inputPayload = job?.input_payload;
  const inputParams = job?.input_params;
  const outputParams = job?.output_params;
  const inputDocument = inputParams?.document;
  const numMaxPaginas = inputParams?.num_max_pagina_a_processar;
  const pagesProcessed = outputParams?.pages;
  const isDone = job?.status?.toUpperCase() === "DONE";
  /* Jobs anteriores ao parâmetro não têm a chave e são normalizados para
     Standard — é o modo em que de fato rodaram. */
  const extractionMode = getJobExtractionMode(job);

  const handleOpenTemplateModal = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setTemplateModalOpen(true);
  };

  const handleCloseTemplateModal = () => {
    setTemplateModalOpen(false);
  };

  // Função para fazer download do JSON do template
  const handleDownloadTemplate = () => {
    if (!inputPayload) return;

    const blob = new Blob([JSON.stringify(inputPayload, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "template.json";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Se não há dados de input, mostrar mensagem
  if (!inputPayload && !inputDocument && !numMaxPaginas) {
    return (
      <Typography className={classes.noData}>
        {t("table.noInput") || "-"}
      </Typography>
    );
  }

  return (
    <Box className={classes.container}>
      {/* Modelo de extração (Standard / Next-Set26). O drawer de
          /processamentos já mostra o modelo na seção "Configuração da
          Execução", então desliga aqui para não repetir; na tela de Status de
          Tarefas esta é a única superfície disponível. */}
      {showExtractionMode && (
        <Box>
          <ExtractionModeChip mode={extractionMode} />
        </Box>
      )}

      {/* Máx páginas a processar e páginas processadas */}
      {(numMaxPaginas !== undefined && numMaxPaginas !== null) ||
      (isDone && pagesProcessed !== undefined && pagesProcessed !== null) ? (
        <Typography className={classes.maxPagesText}>
          {numMaxPaginas !== undefined && numMaxPaginas !== null && (
            <>
              <span className={classes.maxPagesLabel}>
                {t("table.maxPages") || "Máx páginas"}:
              </span>
              {numMaxPaginas}
            </>
          )}
          {isDone &&
            pagesProcessed !== undefined &&
            pagesProcessed !== null && (
              <>
                {numMaxPaginas !== undefined && numMaxPaginas !== null && (
                  <span style={{ margin: "0 4px" }}>|</span>
                )}
                <span className={classes.maxPagesLabel}>
                  {t("table.pagesProcessed") || "Processadas"}:
                </span>
                {pagesProcessed}
              </>
            )}
        </Typography>
      ) : null}

      {/* Link para abrir o modal do Template */}
      {inputPayload && (
        <Link
          component="button"
          className={classes.templateLink}
          onClick={handleOpenTemplateModal}
        >
          <Description className={classes.templateIcon} />
          Template
        </Link>
      )}

      {/* FileDownload para o documento */}
      {inputDocument && inputDocument.filename && (
        <Box className={classes.fileDownloadContainer}>
          <FileDownload
            filename={inputDocument.filename}
            token={inputDocument.token}
            serviceName="PROCESS_DIGITAL_PDF"
            showError={false}
          />
        </Box>
      )}

      {/* Modal de visualização do Template */}
      <Dialog
        open={templateModalOpen}
        onClose={handleCloseTemplateModal}
        maxWidth={false}
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogTitle className={classes.dialogTitle}>
          {t("table.templateStructure") || "Estrutura do Template"}
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <TemplateViewer templateData={inputPayload} height="450px" />
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={handleDownloadTemplate} startIcon={<DownloadIcon />}>
            {t("table.downloadTemplate") || "Baixar JSON"}
          </Button>
          <Button onClick={handleCloseTemplateModal}>
            {t("common:close") || "Fechar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProcessamentoInputCell;
